import { formatInTimeZone } from "date-fns-tz";
import React, { useState } from "react";

import { faEllipsis, faPlus, faRepeat } from "@performyard/fontawesome/pro-solid-svg-icons";
import {
	ButtonStandard,
	Chip,
	DropDownMenu,
	type DropDownMenuOption,
	Icon,
	Text,
	Tooltip,
} from "@performyard/py-components";
import type { ExtendedWorkspace, RecurringSeriesModelJson } from "@performyard/service-contracts";

import { EndRecurringSeriesModal } from "./EndRecurringSeriesModal";

import {
	useDeleteWorkspaceRecurringSeries,
	useRbacObjectPermissions,
	useWorkspaceRecurringSeries,
} from "../../../../api/hooks";
import { useRecurringSeriesMaxCount } from "../hooks";

import { CreateRecurringSeriesModal } from "./modals/CreateRecurringSeriesModal";
import { EditRecurringSeriesModal } from "./modals/EditRecurringSeriesModal";

type RecurringSeriesSectionProps = {
	workspace: ExtendedWorkspace;
};

type RecurringSeriesCardProps = {
	series: RecurringSeriesModelJson;
	onEdit: (_series: RecurringSeriesModelJson) => void;
	onEnd: (_series: RecurringSeriesModelJson) => void;
};

const formatSeriesSchedule = (series: RecurringSeriesModelJson): string => {
	if (series.seriesEndDate) {
		return `${series.recurrenceLabel} until ${formatInTimeZone(
			new Date(series.seriesEndDate),
			series.timezone,
			"MMM d, yyyy"
		)}`;
	}
	return series.recurrenceLabel;
};

/**
 * The already-created upcoming meeting when there is one, else the occurrence
 * PerformYard will create next — `nextOccurrenceAt` alone is an interval ahead of
 * the meeting the user can already see in the table.
 */
const getNextMeetingStart = (series: RecurringSeriesModelJson): string =>
	series.upcomingMeetingStartDateTime ?? series.nextOccurrenceAt;

/**
 * One series in the section. Editing and ending are resolved against the series
 * itself, since they belong to its creator and the workspace creator rather than to
 * everyone who can see the workspace.
 */
const RecurringSeriesCard = ({ series, onEdit, onEnd }: RecurringSeriesCardProps): React.ReactElement => {
	const { data: seriesPermissions } = useRbacObjectPermissions("meeting_recurring_series", series._id);

	const canEditSeries = series.status === "active" && !!seriesPermissions?.includes("meetings:editRecurringSeries");
	const canEndSeries = !!seriesPermissions?.includes("meetings:deleteRecurringSeries");

	const options: DropDownMenuOption[] = [
		...(canEditSeries ? [{ key: "edit", label: "Edit Recurring Meeting" }] : []),
		...(canEndSeries
			? [
					{
						key: "end",
						label: series.status === "active" ? "End Recurring Meeting" : "Remove Recurring Meeting",
					},
				]
			: []),
	];

	const handleOptionSelect = (key: string): void => {
		switch (key) {
			case "edit": {
				onEdit(series);
				break;
			}
			case "end": {
				onEnd(series);
				break;
			}
			default:
				break;
		}
	};

	return (
		<div className="min-w-0 rounded-sm bg-neutral-1 px-4 py-3" data-testid={`recurring-series-card-${series._id}`}>
			<div className="flex items-start justify-between gap-2">
				<Text size="paragraph-3" weight="semi-bold" dangerouslySetClassName="py-line-clamp-1">
					{series.name}
				</Text>
				{options.length > 0 && (
					<DropDownMenu
						options={options}
						onSelect={handleOptionSelect}
						minWidth={200}
						placement="bottom-end"
						testid={`recurring-series-options-${series._id}`}
						ariaLabel={`Options for ${series.name}`}
						className="flex !h-6 !min-h-0 !w-6 shrink-0 !p-0"
					>
						<Icon icon={faEllipsis} size={16} />
					</DropDownMenu>
				)}
			</div>
			<Text size="caption" weight="regular" color="neutral-7">
				{formatSeriesSchedule(series)}
			</Text>
			{series.status === "active" ? (
				<Text size="caption" weight="regular" color="neutral-7">
					Next Meeting:{" "}
					{formatInTimeZone(new Date(getNextMeetingStart(series)), series.timezone, "MMM d, yyyy h:mmaaa")}
				</Text>
			) : (
				<div className="mt-1">
					<Chip label="Ended" color="neutral" size={24} />
				</div>
			)}
		</div>
	);
};

/**
 * The workspace's recurring meetings: a counter against the series cap, one card
 * per series, and the entry points for creating, editing, and ending a series.
 */
export const RecurringSeriesSection = ({ workspace }: RecurringSeriesSectionProps): React.ReactElement => {
	const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
	const [editingSeries, setEditingSeries] = useState<RecurringSeriesModelJson | null>(null);
	const [endingSeries, setEndingSeries] = useState<RecurringSeriesModelJson | null>(null);

	const seriesMaxCount = useRecurringSeriesMaxCount();
	const { data: seriesList, isLoading: isSeriesListLoading } = useWorkspaceRecurringSeries(workspace._id);
	const { data: workspacePermissions } = useRbacObjectPermissions("meeting_workspace", workspace._id);

	const { mutate: deleteSeries, isPending: isDeleting } = useDeleteWorkspaceRecurringSeries(() =>
		setEndingSeries(null)
	);

	// Only workspace members hold meetings:createRecurringSeries; an admin viewing
	// someone else's workspace can read the section but not add to it.
	const canCreateSeries = !!workspacePermissions?.includes("meetings:createRecurringSeries");
	// Until the list settles the count is 0, which would leave the button enabled for
	// a workspace already at its cap.
	const atSeriesCap = isSeriesListLoading || seriesList.length >= seriesMaxCount;

	return (
		<>
			<div
				className={`rounded-lg border border-neutral-2 bg-white px-3 pt-2 ${seriesList.length === 0 ? "pb-2" : "pb-3"}`}
				data-testid="recurring-series-section"
			>
				<div className="flex items-center justify-between">
					<div className="flex items-center gap-1">
						<Icon icon={faRepeat} size={16} color="neutral-5" />
						<Text size="paragraph-2" weight="semi-bold">
							Recurring Meetings
						</Text>
						<Text size="paragraph-3" weight="regular" color="neutral-7">
							·
						</Text>
						<Text size="paragraph-3" weight="regular" color="neutral-7">
							{seriesList.length} of {seriesMaxCount}
						</Text>
					</div>
					{canCreateSeries && (
						// asChild + span: Tooltip renders a button of its own, and a disabled one eats the hover.
						<Tooltip
							content={`A workspace can have at most ${seriesMaxCount} recurring meetings. End one to add another.`}
							placement="top"
							disabled={!atSeriesCap || isSeriesListLoading}
							asChild
						>
							<span className="inline-flex">
								<ButtonStandard
									variant="text"
									color="primary"
									iconLeft={faPlus}
									onClick={() => setIsCreateModalOpen(true)}
									disabled={atSeriesCap}
									data-testid="add-recurring-meeting-button"
								>
									Add Recurring Meeting
								</ButtonStandard>
							</span>
						</Tooltip>
					)}
				</div>
				{seriesList.length > 0 && (
					<div className="mt-1 grid grid-cols-3 gap-2">
						{seriesList.map((series) => (
							<RecurringSeriesCard
								key={series._id}
								series={series}
								onEdit={setEditingSeries}
								onEnd={setEndingSeries}
							/>
						))}
					</div>
				)}
			</div>
			<div className="h-4" />

			<CreateRecurringSeriesModal
				isOpen={isCreateModalOpen}
				handleClose={() => setIsCreateModalOpen(false)}
				workspace={workspace}
			/>
			<EditRecurringSeriesModal
				isOpen={!!editingSeries}
				handleClose={() => setEditingSeries(null)}
				workspaceId={workspace._id}
				series={editingSeries}
			/>
			<EndRecurringSeriesModal
				isOpen={!!endingSeries}
				isLoading={isDeleting}
				seriesName={endingSeries?.name}
				isSeriesEnded={endingSeries?.status !== "active"}
				hasCalendarSeries={!!endingSeries?.hasCalendarSeries}
				onClose={() => setEndingSeries(null)}
				onConfirm={({ removeCalendarEvents }) => {
					if (endingSeries) {
						deleteSeries({ workspaceId: workspace._id, seriesId: endingSeries._id, removeCalendarEvents });
					}
				}}
			/>
		</>
	);
};
